import React, { type ComponentType, type ReactElement, type ReactNode } from 'react';
import { EditAnalyticsWrapper } from '@atlassian/jira-forge-ui-analytics/src/ui/custom-field';
import type {
	CustomField,
	CustomFieldType,
	ForgeCustomFieldValue,
	ForgeUiIssueData,
} from '@atlassian/jira-forge-ui-types';
import type { IssueKey } from '@atlassian/jira-shared-types';
import { useAnalyticsAttributesContext } from '../analytics/atrributes-context';
import type { CustomFieldProps } from '../types';
import EditReadView from './edit-read-view';
import { ForgeCustomFieldEditView } from './view';

type Props = {
	issueKey: IssueKey;
	shouldRenderCustomReadView: boolean;
	value: ForgeCustomFieldValue;
	fieldId: string;
	fieldName: string;
	renderCustomReadView: (newValue: ForgeCustomFieldValue) => ReactElement;
	extension: CustomField | CustomFieldType;
	extensionData: ForgeUiIssueData | null;
	viewProps: CustomFieldProps;
	ForgeField: ComponentType<CustomFieldProps>;
	onCancel: () => void;
	onSave?: (value: ForgeCustomFieldValue | null) => void;
};

export const ForgeCustomFieldEditInner = ({
	renderCustomReadView,
	shouldRenderCustomReadView,
	ForgeField,
	viewProps,
	...restProps
}: Props) => {
	const viewComponent = (
		switchToEditMode: () => void,
		newValue: ForgeCustomFieldValue,
		isLoading: boolean,
	) => {
		const readView = (fieldValue: ForgeCustomFieldValue) => (
			<EditReadView isLoading={isLoading}>{renderCustomReadView(fieldValue)}</EditReadView>
		);

		const fieldContentWrapper = ({
			topMargin,
			children,
		}: {
			topMargin?: number | undefined;
			children: ReactNode;
		}) => (
			<EditReadView
				isCustomReadView={shouldRenderCustomReadView}
				topMargin={topMargin}
				isLoading={isLoading}
			>
				{children}
			</EditReadView>
		);

		const editViewProps = {
			...viewProps,
			onEditRequest: switchToEditMode,
			value: newValue,
			...(shouldRenderCustomReadView ? { customReadView: readView } : { fieldContentWrapper }),
		};

		return <ForgeField {...editViewProps} />;
	};

	return <ForgeCustomFieldEditView {...restProps} viewComponent={viewComponent} />;
};

export const ForgeCustomFieldEdit = (props: Props) => {
	const analyticsAttributes = useAnalyticsAttributesContext();
	return (
		<EditAnalyticsWrapper attributes={analyticsAttributes}>
			<ForgeCustomFieldEditInner {...props} />
		</EditAnalyticsWrapper>
	);
};
